import React, { useMemo } from "react";
import {
  Users,
  CreditCard,
  Wallet,
  Database,
  GraduationCap,
  ChefHat,
  Building2,
  ClipboardList,
  CalendarCheck,
  Banknote,
  Repeat,
  Coins,
  HandCoins,
  CheckCircle2,
  Cog,
  Tent,
  Plus,
  ShieldCheck,
  Network,
  Smartphone,
  ShoppingCart,
  Layers,
} from "lucide-react";
import { flowT } from "./ecolunch_flows";

/* Lucide icon resolver */
const ICONS = {
  Users, CreditCard, Wallet, Database, GraduationCap, ChefHat, Building2,
  ClipboardList, CalendarCheck, Banknote, Repeat, Coins, HandCoins,
  CheckCircle2, Cog, Tent, Plus, ShieldCheck, Network, Smartphone,
  ShoppingCart, Layers,
};

/* Lines stop just inside node borders — bubbles appear to emerge from inside
   the node, but the path does not cross the node's center/text content. */
const RX = 5;
const RY = 3.5;

/* Shrink the segment a→b so it starts/ends slightly inside the node frame (ellipse model). */
const shrinkToBorder = (a, b) => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 0.001) return { a: { ...a }, b: { ...b } };
  const ux = dx / len;
  const uy = dy / len;
  const t = 1 / Math.sqrt((ux * ux) / (RX * RX) + (uy * uy) / (RY * RY));
  return {
    a: { x: a.x + ux * t, y: a.y + uy * t, color: a.color },
    b: { x: b.x - ux * t, y: b.y - uy * t, color: b.color },
  };
};

/* Compute centered SVG path between two nodes.
   Straight line when no arc; quadratic bezier when arc is set (curves around nodes). */
const buildPath = (a, b, opts = {}) => {
  const { a: a2, b: b2 } = shrinkToBorder(a, b);
  const arc = opts.arc || 0;
  if (arc === 0) {
    return `M ${a2.x} ${a2.y} L ${b2.x} ${b2.y}`;
  }
  // Quadratic bezier with control point offset perpendicular to the line by `arc` units
  const mx = (a2.x + b2.x) / 2;
  const my = (a2.y + b2.y) / 2;
  // Perpendicular direction (normalized)
  const dx = b2.x - a2.x;
  const dy = b2.y - a2.y;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const px = -dy / len;
  const py = dx / len;
  const cx = mx + px * arc;
  const cy = my + py * arc;
  return `M ${a2.x} ${a2.y} Q ${cx} ${cy} ${b2.x} ${b2.y}`;
};

/* Get visual position along the path for label placement.
   `t` controls position along the edge (0=start, 0.5=mid, 1=end).
   Accounts for arc offset (curved bezier). */
const pointAlong = (a, b, opts = {}, t = 0.5) => {
  const { a: a2, b: b2 } = shrinkToBorder(a, b);
  const arc = opts.arc || 0;
  const mx = a2.x + (b2.x - a2.x) * t;
  const my = a2.y + (b2.y - a2.y) * t;
  if (arc === 0) return { x: mx, y: my };
  const dx = b2.x - a2.x;
  const dy = b2.y - a2.y;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const px = -dy / len;
  const py = dx / len;
  // bezier perpendicular offset peaks at t=0.5; here we use sin-like factor
  const off = 4 * t * (1 - t); // 0..1, peak at t=0.5
  return { x: mx + px * arc * 0.5 * off, y: my + py * arc * 0.5 * off };
};

const midPoint = (a, b, opts = {}) => pointAlong(a, b, opts, 0.5);

/* Compute label position: shift toward the non-hub endpoint when one side is the hub
   so the label sits clearly outside the hub frame.
   Per-edge override: opts.labelT (0..1) takes priority over the hub heuristic. */
const labelPoint = (a, b, opts = {}) => {
  if (typeof opts.labelT === "number") return pointAlong(a, b, opts, opts.labelT);
  if (a.hub && !b.hub) return pointAlong(a, b, opts, 0.78);
  if (b.hub && !a.hub) return pointAlong(a, b, opts, 0.22);
  return midPoint(a, b, opts);
};

const FlowDiagram = ({ schema, lang = "fr" }) => {
  const { nodes, edges } = schema;
  const nodeMap = useMemo(() => {
    const m = {};
    nodes.forEach((n) => { m[n.id] = n; });
    return m;
  }, [nodes]);

  return (
    <div className="ed-fd" data-testid="flow-diagram">
      {/* SVG layer for edges */}
      <svg
        className="ed-fd-svg"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden
      >
        <defs>
          {edges.map((e, i) => {
            const a = nodeMap[e.from];
            const b = nodeMap[e.to];
            if (!a || !b) return null;
            const { a: a2, b: b2 } = shrinkToBorder(a, b);
            const grad = `efg-${schema.title.replace(/[^a-z0-9]/gi, "")}-${i}`;
            return (
              <linearGradient
                key={grad}
                id={grad}
                gradientUnits="userSpaceOnUse"
                x1={a2.x}
                y1={a2.y}
                x2={b2.x}
                y2={b2.y}
              >
                <stop offset="0%" stopColor={a.color} stopOpacity="0.85" />
                <stop offset="100%" stopColor={b.color} stopOpacity="0.85" />
              </linearGradient>
            );
          })}
        </defs>

        {edges.map((e, i) => {
          const a = nodeMap[e.from];
          const b = nodeMap[e.to];
          if (!a || !b) return null;
          const path = buildPath(a, b, { arc: e.arc });
          const grad = `efg-${schema.title.replace(/[^a-z0-9]/gi, "")}-${i}`;
          return (
            <g key={`edge-${i}`} className="ed-fd-edge">
              <path
                d={path}
                stroke={`url(#${grad})`}
                strokeWidth="0.45"
                fill="none"
                strokeDasharray={e.dashed ? "1.2 1.2" : "none"}
                opacity={e.dashed ? 0.55 : 0.9}
              />
              {/* Animated particle along path */}
              <circle r="0.7" fill={b.color} className="ed-fd-particle">
                <animateMotion
                  dur={`${2.4 + (i % 3) * 0.4}s`}
                  begin={`${i * 0.2}s`}
                  repeatCount="indefinite"
                  path={path}
                />
              </circle>
            </g>
          );
        })}
      </svg>

      {/* Nodes layer (HTML, absolutely positioned in %) */}
      {nodes.map((n) => {
        const Icon = ICONS[n.icon];
        const isHub = n.hub;
        const isFox = n.icon === "fox";
        return (
          <div
            key={n.id}
            className={`ed-fd-node ${isHub ? "hub" : ""} ${n.dim ? "dim" : ""} ${n.compact ? "compact" : ""}`}
            style={{
              left: `${n.x}%`,
              top: `${n.y}%`,
              "--nc": n.color,
            }}
            data-testid={`fd-node-${n.id}`}
          >
            {n.step != null && <span className="ed-fd-step">{n.step}</span>}
            {isHub ? (
              <div className="ed-fd-hub-frame" data-testid="fd-hub-frame">
                <span className="ed-fd-hub-pill">EcoLunch Hub</span>
                <span className="ed-fd-hub-pill ed-fd-hub-pill-mid">EcoTech MID</span>
                <span className="ed-fd-hub-pill">EcoWallet</span>
              </div>
            ) : (
              <div className="ed-fd-node-inner">
                <div className="ed-fd-node-icon">
                  {isFox ? (
                    <img src="/ecolunch-fox.png" alt="hub" />
                  ) : Icon ? (
                    <Icon size={14} strokeWidth={2.2} />
                  ) : null}
                </div>
                <span className="ed-fd-node-label">{flowT(n.label, lang)}</span>
              </div>
            )}
            {n.feeIn && (
              <span className="ed-fd-fee ed-fd-fee-in" title="Fees on this leg">
                {flowT(n.feeIn, lang)}
              </span>
            )}
            {n.feeOut && (
              <span className="ed-fd-fee ed-fd-fee-out" title="Fees on this leg">
                {flowT(n.feeOut, lang)}
              </span>
            )}
            {n.note && <span className="ed-fd-note">{flowT(n.note, lang)}</span>}
          </div>
        );
      })}

      {/* Edge labels layer */}
      <div className="ed-fd-labels">
        {edges.map((e, i) => {
          const a = nodeMap[e.from];
          const b = nodeMap[e.to];
          if (!a || !b || !e.label) return null;
          const m = labelPoint(a, b, { arc: e.arc, labelT: e.labelT });
          return (
            <span
              key={`lbl-${i}`}
              className="ed-fd-edge-label"
              style={{ left: `${m.x}%`, top: `${m.y}%` }}
            >
              {flowT(e.label, lang)}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default FlowDiagram;
